import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import React from 'react';

import {
  formatWizTimestamp,
  parseWizFinding,
} from '@/sections/poe/processors/wiz/helpers';
import type {
  WizParsedData,
  WizRenderContext,
} from '@/sections/poe/processors/wiz/types';
import { isValidExternalURL } from '@/utils/url.util';

export function renderSubscription(
  parsed: WizParsedData,
  ctx: WizRenderContext
): void {
  const { wizData, wizId, isSubscriptionFormat } = parsed;
  const { sections, tempTabs, mainTabKeys } = ctx;

  if (isSubscriptionFormat && mainTabKeys.includes('ATTRIBUTES')) {
    const subscriptionData = parseWizFinding(wizData.subscription);

    sections['wiz_subscription'] = (
      <div className="space-y-4">
        <div className="border-chariot-stroke rounded-lg border">
          <div className="border-chariot-stroke border-b px-4 py-3">
            <h4 className="text-chariot-text-primary font-semibold">
              Cloud Resource
            </h4>
            <span className="border-chariot-stroke bg-chariot-background-secondary text-chariot-text-primary mt-2 inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium">
              Wiz ID: {wizId}
            </span>
          </div>
        </div>

        <div className="border-chariot-stroke rounded-lg border">
          <div className="border-chariot-stroke border-b px-4 py-3">
            <h4 className="text-chariot-text-primary font-semibold">
              AWS Account Details
            </h4>
          </div>
          <div className="space-y-3 p-4">
            {subscriptionData.name && (
              <div className="border-chariot-stroke flex items-start justify-between border-b py-2 last:border-b-0">
                <span className="text-chariot-text-secondary font-medium">
                  Account Name:
                </span>
                <span className="text-chariot-text-primary font-medium">
                  {subscriptionData.name}
                </span>
              </div>
            )}
            {subscriptionData.properties.subscriptionId && (
              <div className="border-chariot-stroke flex items-start justify-between border-b py-2 last:border-b-0">
                <span className="text-chariot-text-secondary font-medium">
                  Account ID:
                </span>
                <code className="bg-chariot-background-secondary text-chariot-text-primary rounded px-2 py-1 text-sm">
                  {subscriptionData.properties.subscriptionId}
                </code>
              </div>
            )}
            {subscriptionData.properties.status && (
              <div className="border-chariot-stroke flex items-start justify-between border-b py-2 last:border-b-0">
                <span className="text-chariot-text-secondary font-medium">
                  Status:
                </span>
                <span
                  className={`rounded px-2 py-1 text-sm font-medium ${
                    subscriptionData.properties.status === 'Active'
                      ? 'bg-success-selected text-success'
                      : 'text-chariot-text-primary'
                  }`}
                >
                  {subscriptionData.properties.status}
                </span>
              </div>
            )}
            {subscriptionData.properties.cloudPlatform && (
              <div className="border-chariot-stroke flex items-start justify-between border-b py-2 last:border-b-0">
                <span className="text-chariot-text-secondary font-medium">
                  Platform:
                </span>
                <span className="text-chariot-text-primary">
                  {subscriptionData.properties.cloudPlatform}
                </span>
              </div>
            )}
            {subscriptionData.properties.creationDate && (
              <div className="border-chariot-stroke flex items-start justify-between border-b py-2 last:border-b-0">
                <span className="text-chariot-text-secondary font-medium">
                  Created:
                </span>
                <span className="text-chariot-text-primary text-sm">
                  {formatWizTimestamp(subscriptionData.properties.creationDate)}
                </span>
              </div>
            )}
            {subscriptionData.properties.updatedAt && (
              <div className="border-chariot-stroke flex items-start justify-between border-b py-2 last:border-b-0">
                <span className="text-chariot-text-secondary font-medium">
                  Last Updated:
                </span>
                <span className="text-chariot-text-primary text-sm">
                  {formatWizTimestamp(subscriptionData.properties.updatedAt)}
                </span>
              </div>
            )}
          </div>
        </div>

        {subscriptionData.properties.path && (
          <div className="border-chariot-stroke rounded-lg border">
            <div className="border-chariot-stroke border-b px-4 py-3">
              <h4 className="text-chariot-text-primary font-semibold">
                Organization Path
              </h4>
            </div>
            <div className="p-4">
              <code className="bg-chariot-background-secondary text-chariot-text-primary block overflow-x-auto rounded p-3 font-mono text-xs break-all">
                {subscriptionData.properties.path}
              </code>
            </div>
          </div>
        )}

        {subscriptionData.providerURL &&
          isValidExternalURL(subscriptionData.providerURL) && (
            <div className="border-chariot-stroke rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <span className="text-chariot-text-secondary font-medium">
                  AWS Console:
                </span>
                <a
                  href={subscriptionData.providerURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-chariot-accent hover:text-chariot-accent-hover flex items-center gap-1 text-sm"
                >
                  Open Account Console
                  <ArrowTopRightOnSquareIcon className="size-3" />
                </a>
              </div>
            </div>
          )}
      </div>
    );
    tempTabs['ATTRIBUTES'].push({
      label: 'Subscription Details',
      value: 'wiz_subscription',
    });
  }
}
